// /case-study/$slug — per-service detailed dossier (not a blog post).
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import type { ServiceDossier } from "@/data/service-dossiers";
import { TrustLayer } from "@/components/TrustLayer";
import ConsultantAdvisoryBlock from "@/components/ConsultantAdvisoryBlock";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { AlertTriangle, CheckCircle2, ExternalLink, FileText } from "lucide-react";
import { dossierHero } from "@/lib/dossierHero";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/case-study/$slug")({
  loader: async ({ params }) => {
    const { DOSSIER_BY_SLUG } = await import("@/data/service-dossiers");
    const d = DOSSIER_BY_SLUG.get(params.slug);
    if (!d) throw notFound();
    return { d };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "ไม่พบหน้ากรณีศึกษา" }, { name: "robots", content: "noindex" }] };
    }
    const { d } = loaderData;
    const url = `${SITE}/case-study/${d.slug}`;
    const hero = dossierHero(d.cluster, SITE);
    const reviewed = new Date().toISOString().slice(0, 10);
    return {
      meta: [
        { title: `${d.metaTitle} | NYC Legal` },
        { name: "description", content: d.metaDescription },
        { property: "og:title", content: d.metaTitle },
        { property: "og:description", content: d.metaDescription },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:image", content: hero.absolute },
        { property: "og:image:alt", content: hero.altTh },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: hero.absolute },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
        { rel: "preload", as: "image", href: hero.src, fetchpriority: "high" },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": `${url}#webpage`,
                url,
                name: d.h1,
                description: d.answer,
                inLanguage: "th",
                datePublished: "2026-08-10",
                dateModified: reviewed,
                primaryImageOfPage: { "@type": "ImageObject", url: hero.absolute, caption: hero.altTh },
                isPartOf: { "@type": "WebSite", "@id": `${SITE}/#website`, url: SITE },
                about: { "@type": "Thing", name: d.title },
                publisher: { "@type": "LegalService", "@id": `${SITE}/#organization`, name: "NYC Language Institute", url: SITE },
                author: {
                  "@type": "Organization",
                  name: "ทีมที่ปรึกษากฎหมายและการแปลรับรอง NYC Language Institute",
                  url: `${SITE}/trust`,
                  description: "ทีมทนายความ Notarial Services Attorney นักแปลรับรอง และที่ปรึกษาด้านเอกสารระหว่างประเทศ ประสบการณ์รวมกว่า 15 ปี",
                },
                speakable: {
                  "@type": "SpeakableSpecification",
                  cssSelector: ["[data-speakable]", "[data-extractive='answer']"],
                },
                breadcrumb: { "@id": `${url}#breadcrumb` },
              },
              {
                "@type": "HowTo",
                "@id": `${url}#howto`,
                name: d.h1,
                description: d.answer,
                inLanguage: "th",
                image: hero.absolute,
                mainEntityOfPage: { "@id": `${url}#webpage` },
                supply: d.documents.map((x) => ({ "@type": "HowToSupply", name: x })),
                step: d.steps.map((s, i) => ({
                  "@type": "HowToStep",
                  position: i + 1,
                  name: s.title,
                  text: `${s.detail} (ผู้ดำเนินการ: ${s.who} · ระยะเวลา: ${s.eta})`,
                })),
              },
              {
                "@type": "FAQPage",
                "@id": `${url}#faq`,
                inLanguage: "th",
                mainEntityOfPage: { "@id": `${url}#webpage` },
                mainEntity: d.faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
              {
                "@type": "BreadcrumbList",
                "@id": `${url}#breadcrumb`,
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                  { "@type": "ListItem", position: 2, name: "กรณีศึกษารายบริการ", item: `${SITE}/case-study` },
                  { "@type": "ListItem", position: 3, name: d.title, item: url },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  component: DossierPage,
});

function DossierPage() {
  const { d } = Route.useLoaderData() as { d: ServiceDossier };
  const hero = dossierHero(d.cluster);
  const reviewed = new Date().toISOString().slice(0, 10);
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep text-white">
        <div className="mx-auto grid max-w-5xl gap-8 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-[1.15fr_1fr] lg:items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
              <FileText className="h-3.5 w-3.5" /> กรณีศึกษารายบริการ
            </div>
            <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-4xl">{d.h1}</h1>
            <p
              className="mt-4 text-base leading-relaxed text-white/90"
              data-speakable="true"
              data-extractive="answer"
            >
              {d.answer}
            </p>
            <p className="mt-3 text-sm text-white/70">เหมาะกับ: {d.audience}</p>
            <p className="mt-2 text-xs text-white/60">
              ตรวจทานโดยทีมที่ปรึกษา (ประสบการณ์รวมกว่า 15 ปี) · ปรับปรุงล่าสุด{" "}
              <time dateTime={reviewed}>{reviewed}</time>
            </p>
          </div>
          <img
            src={hero.src}
            alt={hero.altTh}
            title={hero.altEn}
            width={1200}
            height={800}
            loading="eager"
            decoding="async"
            className="aspect-[3/2] w-full rounded-2xl border border-white/10 object-cover shadow-xl"
          />
        </div>
      </section>


      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
        <section aria-labelledby="facts">
          <h2 id="facts" className="font-display text-2xl font-extrabold text-navy-deep">ข้อเท็จจริงสำคัญ</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[520px] border-collapse text-sm">
              <tbody>
                {d.facts.map((f) => (
                  <tr key={f.label} className="border-b border-slate-200">
                    <th scope="row" className="w-1/3 py-2.5 pr-4 text-left font-semibold text-navy-deep">{f.label}</th>
                    <td className="py-2.5 text-slate-700">{f.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section aria-labelledby="docs" className="mt-10">
          <h2 id="docs" className="font-display text-2xl font-extrabold text-navy-deep">รายการเอกสารที่ต้องเตรียม</h2>
          <ul className="mt-4 space-y-2">
            {d.documents.map((x) => (
              <li key={x} className="flex gap-2 text-slate-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
                <span>{x}</span>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="steps" className="mt-10">
          <h2 id="steps" className="font-display text-2xl font-extrabold text-navy-deep">ขั้นตอนดำเนินการ</h2>
          <ol className="mt-4 space-y-4">
            {d.steps.map((s, i) => (
              <li key={s.title} className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-lg font-extrabold text-amber-600">{i + 1}</span>
                  <h3 className="font-semibold text-navy-deep">{s.title}</h3>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">{s.detail}</p>
                <p className="mt-2 text-xs text-slate-500">ผู้ดำเนินการ: {s.who} · ระยะเวลาโดยประมาณ: {s.eta}</p>
              </li>
            ))}
          </ol>
          <p className="mt-3 text-xs text-slate-500">
            ระยะเวลาทั้งหมดเป็นค่าประมาณจากงานจริง ไม่รวมคิวของหน่วยงานและวันหยุดราชการ
          </p>
        </section>

        <section aria-labelledby="pitfalls" className="mt-10">
          <h2 id="pitfalls" className="font-display text-2xl font-extrabold text-navy-deep">ถูกตีกลับเพราะอะไร</h2>
          <ul className="mt-4 space-y-2">
            {d.pitfalls.map((p) => (
              <li key={p} className="flex gap-2 text-slate-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="compare" className="mt-10">
          <h2 id="compare" className="font-display text-2xl font-extrabold text-navy-deep">เปรียบเทียบเส้นทางที่เลือกได้</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-navy-deep">
                  <th scope="col" className="p-2.5">เส้นทาง</th>
                  <th scope="col" className="p-2.5">เหมาะเมื่อ</th>
                  <th scope="col" className="p-2.5">ระยะเวลา</th>
                  <th scope="col" className="p-2.5">หมายเหตุ</th>
                </tr>
              </thead>
              <tbody>
                {d.compare.map((r) => (
                  <tr key={r.option} className="border-b border-slate-200 align-top">
                    <th scope="row" className="p-2.5 text-left font-semibold text-navy-deep">{r.option}</th>
                    <td className="p-2.5 text-slate-700">{r.useWhen}</td>
                    <td className="p-2.5 text-slate-700">{r.time}</td>
                    <td className="p-2.5 text-slate-600">{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section aria-labelledby="faq" className="mt-10">
          <h2 id="faq" className="font-display text-2xl font-extrabold text-navy-deep">คำถามที่พบบ่อย</h2>
          <div className="mt-4 space-y-4">
            {d.faqs.map((f) => (
              <div key={f.q} className="rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-navy-deep">{f.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <ConsultantAdvisoryBlock locale="th" topic={d.title} />
        <PlanningToolsLinks locale="th" topic={d.title} className="mt-10" />

        <section aria-labelledby="related" className="mt-10">
          <h2 id="related" className="font-display text-xl font-extrabold text-navy-deep">อ่านต่อในหัวข้อที่เกี่ยวข้อง</h2>
          <ul className="mt-3 flex flex-wrap gap-2">
            {d.related.map(([label, path]) => (
              <li key={path}>
                <a href={path} className="rounded-full border border-slate-200 px-3 py-1.5 text-sm text-navy-deep transition hover:border-gold/50 hover:bg-amber-50">
                  {label}
                </a>
              </li>
            ))}
            <li>
              <Link to="/case-study" className="rounded-full border border-slate-200 px-3 py-1.5 text-sm text-navy-deep transition hover:border-gold/50 hover:bg-amber-50">
                กรณีศึกษาทั้งหมด
              </Link>
            </li>
          </ul>
        </section>

        <section className="mt-10 text-sm text-slate-600">
          <h2 className="font-display text-lg font-extrabold text-navy-deep">แหล่งอ้างอิงทางการ</h2>
          <ul className="mt-2 space-y-1">
            {d.citations.map((c) => (
              <li key={c.url}>
                <a href={c.url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1 text-amber-700 underline">
                  {c.label} <ExternalLink className="h-3 w-3" aria-hidden />
                </a>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}

